import { StaticImageData } from "next/image";

export interface ProductBase {
  leadImage: string;
  about: string;
  packageDetails: string;
  keyBenefits: string[];
  keyFeatures: string[];
}

export interface ProductPromo {
  title: string;
  description: string;
  buttonLabel: string;
  playlist: {
    label: string;
    value: string;
    info?: string;
  }[];
}

export interface ProductInformation {
  aboutTitle: string;
  description: string;
}

export interface ProductHowMedicationWorks {
  content: string;
  steps: {
    label: string;
    value: string;
  }[];
}

export interface ProductPrices {
  list: {
    label: string;
    amount: number;
    /**
     * LillyDirect only: the Medicinal Product component of `amount`, meaning
     * the medicine plus the Lilly-funded dispensing and delivery.
     *
     * Onescript cap this per dose (the Maximum Resale Price) and require
     * invoices to itemise it separately from every other fee the clinic
     * charges. It is stored rather than derived because the fee is not a
     * constant: at the agreed prices it is £30 on five doses and £20 on 2.5mg,
     * so subtracting an assumed £30 would understate the 2.5mg medicine to
     * below what the clinic pays for it.
     *
     * Optional: a Standard row has no such component, and a LillyDirect row
     * that predates this has none recorded. Readers must treat absent as
     * "cannot itemise" rather than guessing.
     */
    medicineAmount?: number;
  }[];
}

export interface ProductHowToUse {
  list: {
    label: string;
    description: string;
  }[];
}

export interface ProductImportantInfo {
  label: string;
  content: string;
}

export interface ProductFAQs {
  list: {
    question: string;
    answer: string;
  }[];
}

export interface ProductSEO {
  metatitle: string;
  metadescription: string;
}

/**
 * Which ordering offers a product is sold under, plus the per-offer copy shown
 * to patients. Stored as a product_widget row, widget_type = "OrderingOffers".
 * See tasks/lillydirect-two-tier/02-product-config-ordering-offers.md
 */
export interface OrderingOffers {
  standard: boolean;
  lillydirect: boolean;
  standardDescription: string;
  lillydirectDescription: string;
  lillydirectSkus: {
    label: string;
    sku: string;
  }[];
}

export type OrderingPathway = "standard" | "lillydirect";

export interface ProductData {
  productId: string;
  productName: string;
  slug?: string;
  pgdFormRefId?: string | null;
  productBase: ProductBase;
  productPromo: ProductPromo;
  productInformation: ProductInformation;
  productHowMedicationWorks: ProductHowMedicationWorks;
  productPrices: ProductPrices;
  lillyDirectPrices: ProductPrices;
  orderingOffers: OrderingOffers;
  productHowToUse: ProductHowToUse;
  productImportantInfo: ProductImportantInfo;
  productFAQs: ProductFAQs;
  productSEO: ProductSEO;
}

// Lighter Product type for cart compatibility
export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image?: string;
  category?: string;
  inStock?: boolean;
}

// Helper function to convert ProductData to Product for cart
export function convertToProduct(
  productData: ProductData,
  selectedTreatmentIndex: number = 0,
  /**
   * The price actually on screen, when the caller knows it.
   *
   * The index alone is not enough once a page filters its price list or shows
   * the LillyDirect one, because it then indexes a different array from the
   * Standard list read here. Callers that have the resolved amount should pass
   * it rather than trusting the index to line up.
   */
  selectedAmount?: number
): Product {
  const selectedTreatment =
    productData.productPrices.list[selectedTreatmentIndex] ||
    productData.productPrices.list[0];

  return {
    id: productData.productId,
    name: productData.productName,
    description: productData.productBase.about || "",
    price: selectedAmount ?? (selectedTreatment?.amount || 0),
    image: productData.productBase.leadImage,
    category: productData.productName, // Using product name as category
    inStock: true,
  };
}
